import type { AnalyticsProvider, AnalyticsTrackOptions } from '~analytics/types/analyticsProvider';
import type { RuntimeConfig } from 'nuxt/schema';

import mixpanel from 'mixpanel-browser';

export default class MixpanelProvider implements AnalyticsProvider {
  serviceName: string;
  subscribedEvents: string[];
  config: RuntimeConfig;
  enableCustomFlushInterval: boolean;
  batchFlushIntervalMs: number;

  constructor(
    serviceName: string,
    config: RuntimeConfig,
    subscribedEvents: string[],
    enableCustomFlushInterval: boolean = false,
    batchFlushIntervalMs: number = 5000,
  ) {
    this.serviceName = serviceName;
    this.subscribedEvents = subscribedEvents;
    this.config = config;
    this.enableCustomFlushInterval = enableCustomFlushInterval;
    this.batchFlushIntervalMs = batchFlushIntervalMs;
  }

  init() {
    if (!this.config.public.mixpanelToken) {
      console.warn('MixpanelProvider initialized without public mixpanelToken');
    }

    const apiTransportConfig = this.enableCustomFlushInterval
      ? { api_transport: 'sendBeacon', batch_flush_interval_ms: this.batchFlushIntervalMs }
      : {};

    mixpanel.init(this.config.public.mixpanelToken, { api_host: 'https://mp.app.anyway.ai', ...apiTransportConfig });
  }

  track(eventName: string, eventData?: object, options?: AnalyticsTrackOptions) {
    // With batching on (see `enableCustomFlushInterval`) a tracked event sits in a queue for up
    // to `batchFlushIntervalMs`. An event fired as the tab is being hidden or closed would die
    // there, so `immediate` opts that event out of the queue.
    if (options?.immediate) {
      mixpanel.track(eventName, eventData, { send_immediately: true, transport: 'sendBeacon' });
      return;
    }

    mixpanel.track(eventName, eventData);
  }

  identify(guid: string | undefined) {
    mixpanel.identify(guid);
  }

  reset() {
    mixpanel.reset();
  }

  setUserProperties(properties: Record<string, unknown>) {
    mixpanel.register(properties);
    mixpanel.people.set(properties);
  }

  getSubscribedEvents() {
    return this.subscribedEvents;
  }
}
